/**
 * Google reCAPTCHA v3 (invisible, score-based) helper.
 *
 * Disabled automatically when no site key is configured or when running in the
 * preview/E2E environment (REACT_APP_E2E_MODE=true) so automated testing and
 * local flows are never blocked. In production it loads the v3 script and
 * returns a fresh token per action for the backend to verify.
 */
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const E2E = process.env.REACT_APP_E2E_MODE === 'true';

let scriptPromise = null;

export function recaptchaEnabled() {
  return !!SITE_KEY && !E2E;
}

function loadScript() {
  if (!recaptchaEnabled()) return Promise.resolve(null);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha && window.grecaptcha.execute) {
      resolve(window.grecaptcha);
      return;
    }
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.grecaptcha);
    s.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// Preload the script so the first submit is fast.
if (recaptchaEnabled()) {
  loadScript().catch(() => {});
}

/**
 * Execute reCAPTCHA v3 for the given action and return a token.
 * Returns '' when captcha is disabled (preview/E2E) or on any failure — the
 * backend treats an empty token as "no captcha" and, in production, will reject.
 */
export async function executeRecaptcha(action) {
  if (!recaptchaEnabled()) return '';
  try {
    const grecaptcha = await loadScript();
    if (!grecaptcha) return '';
    await new Promise((res) => grecaptcha.ready(res));
    return await grecaptcha.execute(SITE_KEY, { action });
  } catch (e) {
    console.warn('reCAPTCHA execute failed', e);
    return '';
  }
}
